import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Button from '@liquidweb-dev/undercurrent/Button';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import toast from '@liquidweb-dev/undercurrent/toast';
import DialogDestroyAddon, {
	DialogDestroyAddonProps,
} from 'components/DialogDestroyAddon';
import { CloudBackUpAddon } from 'components/DialogDestroyAddon/AddOnType';
import CancellationAlert from 'components/DialogDestroyAddon/CancellationAlert';
import DestroyConfirmation from 'components/DialogDestroyAddon/DestroyConfirmation';
import dayjs from 'dayjs';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useServerUpdate from 'modules/queries/server/useUpdate';
import { useState } from 'react';
import { dateMonthYear } from 'utility/constants/dateTime';

export interface DialogDestroyCloudBackupProps
	extends Omit<DialogDestroyAddonProps, 'title' | 'subtitle'> {
	uniqId: string;
	domain?: string;
}

const DialogDestroyCloudBackup = ({
	uniqId,
	domain,
	onClose,
	...dialogProps
}: DialogDestroyCloudBackupProps) => {
	const title = `Cloud Backups: ${domain}`;

	const [isDestroyConfirmationOpen, setDestroyConfirmationOpen] =
		useState(false);
	const { mutate: updateServer, isLoading } = useServerUpdate();

	const { data: assetDetails } = useAssetDetails({
		uniq_id: uniqId,
		alsowith: [
			'capabilities',
			'associationDetails',
			'featureDetails',
			'nextInvoiceDate',
			'scheduledCancellationDate',
		],
	});

	const canScheduleTerminate = fromApiBoolean(
		assetDetails?.capabilities?.canScheduleTerminate,
	);
	/**
	 * An asset that cannot have its termination scheduled is destroyed as soon
	 * as the customer confirms, so it ends today rather than at the next
	 * invoice.
	 */
	const cancellationDate = (
		canScheduleTerminate
			? dayjs(assetDetails?.nextInvoiceDate?.DateTime)
			: dayjs()
	).format(dateMonthYear);

	const handleDestroyConfirmation = () => {
		updateServer(
			{
				uniq_id: uniqId,
				features: { LiquidWebBackupPlan: 'None' },
			},
			{
				onSuccess: () => {
					toast.success('Service cancelled', {
						description: `Service for ${title} has been cancelled.`,
					});
					onClose();
				},
			},
		);
	};

	const handleClose = () => {
		setDestroyConfirmationOpen(false);
		onClose();
	};

	return (
		<DialogDestroyAddon onClose={handleClose} {...dialogProps}>
			{!isDestroyConfirmationOpen ? (
				<Stack sx={{ gap: 400 }}>
					<Stack sx={{ gap: 200 }}>
						<Stack sx={{ gap: 400 }}>
							<CancellationAlert
								cancellationDate={cancellationDate}
								canScheduleTerminate={canScheduleTerminate}
							/>
							<Stack
								sx={{
									gap: 400,
								}}
							>
								<CloudBackUpAddon uniqId={uniqId} domain={domain} />
							</Stack>
						</Stack>
						<Stack sx={{ gap: 100 }}>
							<Button
								size="large"
								fullWidth
								disabled={isLoading}
								loading={isLoading}
								onClick={() => setDestroyConfirmationOpen(true)}
							>
								Continue
							</Button>
							<Button variant="secondary" size="large" onClick={onClose}>
								Keep service
							</Button>
						</Stack>
					</Stack>
				</Stack>
			) : (
				<DestroyConfirmation
					isLoading={isLoading}
					uniqId={uniqId}
					addonName={title}
					onDestroy={handleDestroyConfirmation}
					onBack={() => setDestroyConfirmationOpen(false)}
				/>
			)}
		</DialogDestroyAddon>
	);
};

export default DialogDestroyCloudBackup;
